import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * HUB button — mirrors the Figma `button-md` / `button-lg` components.
 *
 * size:  md = px 16 / py 8  → 36px tall (desktop, ≥1200px)
 *        lg = px 24 / py 12 → 44px tall (below 1200px)
 * shape: default = radius/xs (4px), pill = radius/full
 */
const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap hub-label-md transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-canvas disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-btn-primary text-btn-text-inverse hover:opacity-90',
        /** secondary sitting on a raised surface (inside a card) */
        secondary: 'bg-btn-secondary-raised text-btn-text hub-stroke hover:brightness-95',
        /** secondary sitting directly on the canvas */
        'secondary-base':
          'bg-btn-secondary-base text-btn-text hub-stroke hover:bg-btn-border dark:hover:bg-btn-border',
        ghost: 'text-btn-text hover:bg-btn-secondary-raised',
        link: 'text-link underline underline-offset-4',
      },
      size: {
        md: 'px-4 py-2',
        lg: 'px-6 py-3',
        /** Figma default: lg below 1200px, md from 1200px up. */
        responsive: 'px-6 py-3 lg:px-4 lg:py-2',
        icon: 'size-9 p-0',
      },
      shape: {
        default: 'rounded-xs',
        pill: 'rounded-full',
        /** radius/xl — used by the cookie banner buttons. */
        xl: 'rounded-xl',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      shape: 'default',
      block: false,
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export function Button({
  className,
  variant,
  size,
  shape,
  block,
  asChild = false,
  ...props
}: ButtonProps): React.ReactElement {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp className={cn(buttonVariants({ variant, size, shape, block }), className)} {...props} />
  )
}

export { buttonVariants }
